// Befehlspalette (Cmd+K): Seiten, Aktionen und – je Welle mehr – Objekte, Verträge, Dokumente.
import { useQueryClient } from "@tanstack/react-query";
import { Command, defaultFilter } from "cmdk";
import { ArrowRight, LogOut, Monitor, Moon, Sun, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, daten, fehlertext } from "../api/client";
import { useRueckmeldung } from "../bausteine/rueckmeldung";
import { useSitzung } from "../kern/sitzung";
import { themaSetzen, type Thema } from "../kern/thema";
import { BEREICHE, SEITEN } from "../navigation/katalog";
import { Symbol } from "../navigation/Symbol";

type Eintrag = { id: string; gruppe: string; titel: string; zusatz: string; begriffe: string[]; symbol?: LucideIcon;
  bereichSymbol?: (typeof BEREICHE)[number]["symbol"]; los: () => void };

const klein = (t: string) => t.toLocaleLowerCase("de-DE").trim();
const VERSTECKT = new Set(SEITEN.filter((s) => s.versteckt).map((s) => klein(s.titel)));

/** Versteckte Seiten (Bausteine) erscheinen nur bei genau ihrem Titel. */
// eslint-disable-next-line react-refresh/only-export-components
export function verstecktSichtbar(titel: string, suche: string): boolean {
  return !VERSTECKT.has(klein(titel)) || klein(titel) === klein(suche);
}

/** Bewertung für cmdk: Treffer im Titel vor Treffern in Beschreibung und Suchbegriffen – „Abmelden“ oder „Helle
 *  Darstellung“ wählen die Aktion, nicht eine Seite, deren Beschreibung zufällig ähnlich klingt. Der Titel kommt als
 *  erstes Stichwort mit (keywords[0]). Versteckte Seiten bekommen ohne genauen Titel den kleinsten Wert statt 0: so
 *  bleibt ihr Eintrag im DOM und wird mitsortiert (cmdk sortiert nur vorhandene Knoten – sonst stünde „Bausteine“ beim
 *  letzten Tastendruck unsortiert und Enter öffnete den alten ersten Treffer); unsichtbar und nicht wählbar macht ihn
 *  verstecktSichtbar. */
// eslint-disable-next-line react-refresh/only-export-components
export function bewerten(wert: string, suche: string, stichworte?: string[]): number {
  const s = klein(suche);
  if (!s) return 1;
  const titel = klein(stichworte?.[0] ?? wert);
  if (VERSTECKT.has(titel)) return titel === s ? 1 : 0.0001;
  let punkte: number;
  if (titel === s) punkte = 1;
  else if (titel.startsWith(s)) punkte = 0.95;
  else if (titel.split(/[\s›&/–-]+/).some((w) => w.startsWith(s))) punkte = 0.9;
  else punkte = Math.min(0.8, defaultFilter(wert, suche, stichworte) * 0.8);
  return punkte;
}

/** `gehe` navigiert: im Programm innerhalb der Website, im Rahmen über den Rahmen (lädt ggf. ein anderes Programm). */
export function Befehlspalette({ offen, setOffen, gehe }: { offen: boolean; setOffen: (o: boolean) => void; gehe: (pfad: string) => void }) {
  const { darfEines, abmelden } = useSitzung();
  const [suche, setSuche] = useState("");
  const client = useQueryClient();
  const { melden } = useRueckmeldung();

  // Die Wahl wirkt sofort und wird für den Benutzer gespeichert – wie das Segment in den Einstellungen. Sonst übernähme
  // das Cockpit bei der nächsten Sitzung wieder den alten Serverstand (Prüfung R3).
  const thema = useCallback((t: Thema) => {
    themaSetzen(t);
    daten(api.PUT("/api/system/ich/einstellungen", { body: { thema: t } }))
      .then((r) => client.setQueryData(["ich", "einstellungen"], r))
      .catch((f: unknown) => melden({ art: "kritisch", titel: "Darstellung nicht gespeichert", text: fehlertext(f) }));
  }, [client, melden]);

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOffen(!offen);
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [offen, setOffen]);

  useEffect(() => {
    if (!offen) setSuche("");
  }, [offen]);

  const eintraege = useMemo<Eintrag[]>(() => {
    const zu = (fn: () => void) => () => { setOffen(false); fn(); };
    const seiten: Eintrag[] = SEITEN.filter((s) => darfEines(s.rechte)).map((s) => {
      const bereich = BEREICHE.find((b) => b.id === s.bereich);
      return {
        id: `seite:${s.pfad}`, gruppe: "Seiten", titel: s.titel,
        zusatz: bereich && bereich.titel !== s.titel ? bereich.titel : "",  // Startseite: nicht „Start · Start“
        begriffe: [s.beschreibung, ...(s.suchbegriffe ?? []), bereich?.titel ?? ""], bereichSymbol: bereich?.symbol,
        los: zu(() => gehe(s.pfad)),
      };
    });
    const aktionen: Eintrag[] = [
      { id: "thema:dunkel", gruppe: "Aktionen", titel: "Dunkle Darstellung", zusatz: "Darstellung", begriffe: ["Theme", "dark"],
        symbol: Moon, los: zu(() => thema("dunkel")) },
      { id: "thema:hell", gruppe: "Aktionen", titel: "Helle Darstellung", zusatz: "Darstellung", begriffe: ["Theme", "light"],
        symbol: Sun, los: zu(() => thema("hell")) },
      { id: "thema:system", gruppe: "Aktionen", titel: "Darstellung wie das System", zusatz: "Darstellung",
        begriffe: ["Theme", "automatisch"], symbol: Monitor, los: zu(() => thema("system")) },
      { id: "abmelden", gruppe: "Aktionen", titel: "Abmelden", zusatz: "Sitzung", begriffe: ["Logout"], symbol: LogOut,
        los: zu(() => { void abmelden().then(() => gehe("/anmelden")); }) },
    ];
    return [...seiten, ...aktionen];
  }, [darfEines, gehe, abmelden, setOffen, thema]);

  const gruppen = useMemo(() => {
    const m = new Map<string, Eintrag[]>();
    for (const e of eintraege) m.set(e.gruppe, [...(m.get(e.gruppe) ?? []), e]);
    return [...m.entries()];
  }, [eintraege]);

  const eintragZeile = (e: Eintrag) => {
    const sichtbar = verstecktSichtbar(e.titel, suche);
    return (
    <Command.Item key={e.id} value={`${e.titel} ${e.zusatz}`} keywords={[e.titel, ...e.begriffe]} onSelect={e.los}
      className="im-cmd__item" disabled={!sichtbar} hidden={!sichtbar} style={sichtbar ? undefined : { display: "none" }}>
      {e.symbol ? <e.symbol size={16} strokeWidth={1.5} aria-hidden="true" />
        : e.bereichSymbol ? <Symbol name={e.bereichSymbol} /> : <ArrowRight size={16} strokeWidth={1.5} />}
      <span>{e.titel}</span>
      <small>{e.zusatz}</small>
    </Command.Item>
    );
  };
  // „Nichts gefunden“ selbst bestimmen: ein unsichtbarer versteckter Eintrag zählt für cmdk sonst als Treffer
  const keinTreffer = !!suche.trim() && !eintraege.some((e) => verstecktSichtbar(e.titel, suche)
    && bewerten(`${e.titel} ${e.zusatz}`, suche, [e.titel, ...e.begriffe]) > 0);

  return (
    <Command.Dialog open={offen} onOpenChange={setOffen} label="Befehlspalette" className="im-cmd im-cmd--dialog"
      overlayClassName="im-cmd-overlay" contentClassName="im-cmd-inhalt" loop filter={bewerten}>
      <div className="im-cmd__input">
        <Symbol name="suche" />
        <Command.Input value={suche} onValueChange={setSuche} placeholder="Seite, Aktion oder Schlüssel suchen …"
          aria-label="Suchen" />
        <span className="im-kbd">Esc</span>
      </div>
      <Command.List className="im-cmd__liste">
        {keinTreffer && <div className="im-cmd__leer" role="status">Nichts gefunden für „{suche}“.</div>}
        {/* Mit Suche eine flache Liste: cmdk sortiert Einträge nur innerhalb ihres Behälters (Gruppen sortiert es nicht
            zuverlässig) – so steht der beste Treffer oben, egal ob Seite oder Aktion. Ohne Suche nach Gruppen. */}
        {suche.trim() ? eintraege.map(eintragZeile) : gruppen.map(([gruppe, liste]) => (
          <Command.Group key={gruppe} heading={gruppe} className="im-cmd__group">{liste.map(eintragZeile)}</Command.Group>
        ))}
      </Command.List>
      <div className="im-cmd__foot">
        <span><span className="im-kbd">↑↓</span> wählen</span>
        <span><span className="im-kbd">↵</span> öffnen</span>
        <span><span className="im-kbd">⌘K</span> schließen</span>
      </div>
    </Command.Dialog>
  );
}
